import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAddMember,
  useAddRoleMapping,
  useAnonymizeUser,
  useGuildRoles,
  useMembers,
  useRemoveMember,
  useRemoveRoleMapping,
  type AnonymizeResult,
  type DerivedAdmin,
} from '../../../lib/membersQueries.js';
import { useGuildConfig } from '../../../lib/auditQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { UserPicker } from '../../../components/UserPicker.js';
import { MemberLabel } from '../../../components/MemberLabel.js';

export const Route = createFileRoute('/guilds/$guildId/members')({
  component: MembersPage,
});

const SNOWFLAKE_RE = /^\d{17,20}$/;

function MembersPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const members = useMembers(guildId);
  const roles = useGuildRoles(guildId);
  const guildConfig = useGuildConfig(guildId);

  const roleNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of roles.data ?? []) m.set(r.id, r.name);
    return m;
  }, [roles.data]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
          <Link to="/" className="shrink-0">
            <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
          </Link>
          <Breadcrumbs
            items={[
              { label: t('app.name'), to: '/' },
              {
                label: guildConfig.data?.name ?? guildId,
                to: '/guilds/$guildId',
                params: { guildId },
              },
              { label: t('members.title') },
            ]}
          />
          <div className="ml-auto">
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-10 px-6 py-8">
        <UsersSection
          guildId={guildId}
          members={members.data?.members ?? []}
          loading={members.isLoading}
        />
        <DerivedAdminsSection
          guildId={guildId}
          derived={members.data?.derivedAdmins ?? []}
          loading={members.isLoading}
        />
        <RoleMappingsSection
          guildId={guildId}
          mappings={members.data?.roleMappings ?? []}
          roles={roles.data ?? []}
          roleNames={roleNames}
          loading={members.isLoading || roles.isLoading}
        />
        <AnonymizeSection guildId={guildId} />
      </main>
    </div>
  );
}

function UsersSection({
  guildId,
  members,
  loading,
}: {
  guildId: string;
  members: { userId: string; role: 'admin' | 'mod'; addedAt: string }[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<'admin' | 'mod'>('mod');
  const [error, setError] = useState<string | null>(null);
  const addMember = useAddMember(guildId);
  const removeMember = useRemoveMember(guildId);

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
        {t('members.users.title')}
      </h2>
      <div className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!SNOWFLAKE_RE.test(userId)) {
              setError(t('members.users.snowflakeError'));
              return;
            }
            addMember.mutate(
              { userId, role },
              {
                onSuccess: () => {
                  setUserId('');
                },
                onError: (err) => {
                  setError(err instanceof Error ? err.message : t('members.users.addError'));
                },
              },
            );
          }}
        >
          <label className="flex flex-col text-xs">
            <span className="mb-1 text-zinc-400">{t('members.users.userId')}</span>
            <div className="w-72">
              <UserPicker guildId={guildId} value={userId} onChange={setUserId} />
            </div>
          </label>
          <label className="flex flex-col text-xs">
            <span className="mb-1 text-zinc-400">{t('members.users.role')}</span>
            <select
              className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
              value={role}
              onChange={(e) => {
                setRole(e.target.value as 'admin' | 'mod');
              }}
            >
              <option value="mod">{t('members.role.mod')}</option>
              <option value="admin">{t('members.role.admin')}</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={addMember.isPending}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {t('members.users.addButton')}
          </button>
        </form>
        {error ? <p className="text-xs text-rose-400">{error}</p> : null}

        {loading ? (
          <p className="text-sm text-zinc-400">{t('common.loading')}</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-zinc-400">{t('members.users.empty')}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="py-2">{t('members.users.userId')}</th>
                <th className="py-2">{t('members.users.role')}</th>
                <th className="py-2">{t('members.users.addedAt')}</th>
                <th className="py-2 text-right">{t('members.users.action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {members.map((m) => (
                <tr key={m.userId}>
                  <td className="py-2">
                    <MemberLabel guildId={guildId} userId={m.userId} compact />
                  </td>
                  <td className="py-2">
                    <span
                      className={`rounded-md border px-2 py-0.5 text-xs font-medium ${
                        m.role === 'admin'
                          ? 'border-indigo-800 bg-indigo-900/40 text-indigo-200'
                          : 'border-emerald-800 bg-emerald-900/40 text-emerald-200'
                      }`}
                    >
                      {t(`members.role.${m.role}`)}
                    </span>
                  </td>
                  <td className="py-2 text-xs text-zinc-400">
                    {new Date(m.addedAt).toLocaleString()}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(t('members.users.removeConfirm', { userId: m.userId }))) {
                          removeMember.mutate(m.userId);
                        }
                      }}
                      className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800"
                    >
                      {t('common.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function DerivedAdminsSection({
  guildId,
  derived,
  loading,
}: {
  guildId: string;
  derived: DerivedAdmin[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
        {t('members.derived.title')}
      </h2>
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
        <p className="mb-3 text-xs text-zinc-500">{t('members.derived.hint')}</p>
        {loading ? (
          <p className="text-sm text-zinc-400">{t('common.loading')}</p>
        ) : derived.length === 0 ? (
          <p className="text-sm text-zinc-400">{t('members.derived.empty')}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="py-2">{t('members.users.userId')}</th>
                <th className="py-2">{t('members.derived.source')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {derived.map((d) => (
                <tr key={d.userId}>
                  <td className="py-2">
                    <MemberLabel guildId={guildId} userId={d.userId} compact />
                  </td>
                  <td className="py-2">
                    <span
                      className="rounded-md border border-amber-800 bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-200"
                      title={t('members.derived.tooltip')}
                    >
                      {t(
                        d.source === 'discord-owner'
                          ? 'members.derived.owner'
                          : 'members.derived.administrator',
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function AnonymizeSection({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const [purgePii, setPurgePii] = useState(false);
  const anonymize = useAnonymizeUser(guildId);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnonymizeResult | null>(null);

  const valid = SNOWFLAKE_RE.test(userId);

  return (
    <section>
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-400">
        {t('members.anonymize.title')}
      </h2>
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
        <p className="mb-3 text-xs text-rose-200/80">{t('members.anonymize.warning')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-zinc-400">
            {t('members.anonymize.userId')}
            <div className="mt-1 w-64">
              <UserPicker
                guildId={guildId}
                value={userId}
                onChange={(id) => {
                  setUserId(id);
                  setError(null);
                  setResult(null);
                }}
              />
            </div>
          </label>
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
              checked={purgePii}
              onChange={(e) => {
                setPurgePii(e.target.checked);
              }}
            />
            {t('members.anonymize.purgePii')}
          </label>
          <button
            type="button"
            disabled={!valid || anonymize.isPending}
            onClick={() => {
              if (!valid) {
                setError(t('members.users.snowflakeError'));
                return;
              }
              if (
                !window.confirm(
                  t('members.anonymize.confirm', {
                    userId,
                    extra: purgePii ? t('members.anonymize.confirmPurge') : '',
                  }),
                )
              ) {
                return;
              }
              anonymize.mutate(
                { userId, purgePii },
                {
                  onSuccess: (data) => {
                    setResult(data);
                    setUserId('');
                  },
                  onError: (err: unknown) => {
                    setError(err instanceof Error ? err.message : 'error');
                  },
                },
              );
            }}
            className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-50"
          >
            {anonymize.isPending ? t('common.loading') : t('members.anonymize.button')}
          </button>
        </div>
        {error ? <p className="mt-2 text-xs text-rose-300">{error}</p> : null}
        {result ? (
          <p className="mt-2 text-xs text-emerald-300">
            {t('members.anonymize.success', {
              audit: result.auditLogs,
              configAudit: result.configAudit,
              purges: result.purgeMods + result.purgeFilter,
              sessions: result.sessions,
              members: result.members,
              infractions: result.infractions,
            })}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function RoleMappingsSection({
  guildId,
  mappings,
  roles,
  roleNames,
  loading,
}: {
  guildId: string;
  mappings: { roleId: string; role: 'admin' | 'mod' }[];
  roles: { id: string; name: string; managed: boolean }[];
  roleNames: Map<string, string>;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [roleId, setRoleId] = useState('');
  const [botRole, setBotRole] = useState<'admin' | 'mod'>('mod');
  const [error, setError] = useState<string | null>(null);
  const addMapping = useAddRoleMapping(guildId);
  const removeMapping = useRemoveRoleMapping(guildId);

  const mappedSet = useMemo(() => new Set(mappings.map((m) => m.roleId)), [mappings]);
  const selectableRoles = roles.filter((r) => !r.managed && !mappedSet.has(r.id));

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
        {t('members.roleMappings.title')}
      </h2>
      <div className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!roleId) {
              setError(t('members.roleMappings.selectError'));
              return;
            }
            addMapping.mutate(
              { roleId, role: botRole },
              {
                onSuccess: () => {
                  setRoleId('');
                },
                onError: (err) => {
                  setError(err instanceof Error ? err.message : t('members.roleMappings.addError'));
                },
              },
            );
          }}
        >
          <label className="flex flex-col text-xs">
            <span className="mb-1 text-zinc-400">{t('members.roleMappings.discordRole')}</span>
            <select
              className="w-72 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
              value={roleId}
              onChange={(e) => {
                setRoleId(e.target.value);
              }}
            >
              <option value="">{t('members.roleMappings.select')}</option>
              {selectableRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs">
            <span className="mb-1 text-zinc-400">{t('members.roleMappings.botRole')}</span>
            <select
              className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
              value={botRole}
              onChange={(e) => {
                setBotRole(e.target.value as 'admin' | 'mod');
              }}
            >
              <option value="mod">{t('members.role.mod')}</option>
              <option value="admin">{t('members.role.admin')}</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={addMapping.isPending}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {t('members.roleMappings.addButton')}
          </button>
        </form>
        {error ? <p className="text-xs text-rose-400">{error}</p> : null}

        {loading ? (
          <p className="text-sm text-zinc-400">{t('common.loading')}</p>
        ) : mappings.length === 0 ? (
          <p className="text-sm text-zinc-400">{t('members.roleMappings.empty')}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="py-2">{t('members.roleMappings.role')}</th>
                <th className="py-2">{t('members.roleMappings.botRole')}</th>
                <th className="py-2 text-right">{t('members.roleMappings.action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {mappings.map((m) => (
                <tr key={m.roleId}>
                  <td className="py-2">
                    {roleNames.get(m.roleId) ?? (
                      <code className="font-mono text-xs">{m.roleId}</code>
                    )}
                  </td>
                  <td className="py-2">
                    <span
                      className={`rounded-md border px-2 py-0.5 text-xs font-medium ${
                        m.role === 'admin'
                          ? 'border-indigo-800 bg-indigo-900/40 text-indigo-200'
                          : 'border-emerald-800 bg-emerald-900/40 text-emerald-200'
                      }`}
                    >
                      {t(`members.role.${m.role}`)}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        const name = roleNames.get(m.roleId) ?? m.roleId;
                        if (confirm(t('members.roleMappings.removeConfirm', { name }))) {
                          removeMapping.mutate(m.roleId);
                        }
                      }}
                      className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800"
                    >
                      {t('common.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
